import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import {
    createOrganizationMember,
    deleteOrganizationMember,
    listOrganizationMembers,
    listOrganizations,
    listOrgRoles,
    listUsers,
} from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { OrganizationMemberDetailPanel } from '../../components/detail/OrganizationMemberDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { RemoveEntityAction } from '../../components/RemoveEntityAction';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import { inlineOrganizationForMember, organizationByIdMap } from '../../lib/organizationLookup';
import type { OrganizationMember } from '../../types/api';

export function OrganizationMembersPage() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [orgFilter, setOrgFilter] = useState<string | null>(null);
    const [searchQ, setSearchQ] = useState('');
    const [detail, setDetail] = useState<OrganizationMember | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const queryClient = useQueryClient();
    const lookups = useAdminLookups();

    const { data, isLoading } = useListQuery('organization-members', listOrganizationMembers, {
        page,
        ...(orgFilter ? { organizationId: orgFilter } : {}),
        ...(searchQ ? { q: searchQ } : {}),
    });

    const orgsQuery = useQuery({
        queryKey: ['organizations-all'],
        queryFn: () => listOrganizations({ limit: 500, page: 1 }),
    });

    const orgById = useMemo(
        () => organizationByIdMap(orgsQuery.data?.data ?? []),
        [orgsQuery.data],
    );

    const resolveMemberOrg = (member: OrganizationMember) =>
        lookups.resolveOrganization(
            member.organizationId,
            inlineOrganizationForMember(member, orgById),
        );
    const usersQuery = useQuery({
        queryKey: ['users-all'],
        queryFn: () => listUsers({ limit: 200, page: 1 }),
    });

    const form = useForm({
        initialValues: {
            mode: 'existing' as 'existing' | 'new',
            userId: '',
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            organizationId: '',
            roleId: '',
        },
    });

    // The member's role must be one of the selected organization's own roles
    // (authored in the Org roles tab), not another org's roles or system templates.
    const selectedOrgId = form.values.organizationId;
    const orgRolesQuery = useQuery({
        queryKey: ['org-roles', selectedOrgId],
        queryFn: () => listOrgRoles(selectedOrgId),
        enabled: Boolean(selectedOrgId),
    });
    const orgRoles = orgRolesQuery.data?.roles ?? [];

    const createMutation = useMutation({
        mutationFn: () => {
            const base = {
                organizationId: form.values.organizationId,
                roleId: form.values.roleId,
            };
            if (form.values.mode === 'existing') {
                return createOrganizationMember({ ...base, userId: form.values.userId });
            }
            return createOrganizationMember({
                ...base,
                user: {
                    email: form.values.email,
                    firstName: form.values.firstName,
                    lastName: form.values.lastName,
                    phone: form.values.phone,
                },
            });
        },
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.orgMembers.added') });
            setCreateOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['organization-members'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.orgMembers.title')}
                description={t('features.orgMembers.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.orgMembers.addAction')}
                    </Button>
                }
            />
            <DataPanel>
                <Group align="flex-end" grow>
                    <Select
                        label={t('features.orgMembers.filterOrg')}
                        placeholder={t('features.orgMembers.filterOrgPlaceholder')}
                        clearable
                        searchable
                        data={(orgsQuery.data?.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
                        value={orgFilter}
                        onChange={(v) => {
                            setOrgFilter(v);
                            setPage(1);
                        }}
                    />
                    <TextInput
                        label={t('common.search.label')}
                        placeholder={t('features.orgMembers.searchPlaceholder')}
                        value={searchQ}
                        onChange={(e) => {
                            setSearchQ(e.currentTarget.value);
                            setPage(1);
                        }}
                    />
                </Group>
            </DataPanel>
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'user',
                        header: t('fields.user'),
                        render: (r) => {
                            const u = lookups.resolveUser(r.userId, r.user);
                            return (
                                <EntityRef
                                    title={u.title}
                                    id={u.id}
                                    secondary={u.secondary}
                                    href={`/users?id=${r.userId}`}
                                />
                            );
                        },
                    },
                    {
                        key: 'org',
                        header: t('fields.organization'),
                        render: (r) => {
                            const o = resolveMemberOrg(r);
                            return (
                                <EntityRef
                                    title={o.title}
                                    id={o.id}
                                    href={`/organizations?id=${r.organizationId}`}
                                    hideId
                                />
                            );
                        },
                    },
                    {
                        key: 'role',
                        header: t('fields.role'),
                        render: (r) => {
                            const link = r.memberRoles?.[0];
                            const role = lookups.resolveRole(
                                link?.roleId ?? r.roleId,
                                link?.role ?? r.role,
                            );
                            return <EntityRef title={role.title} id={role.id} />;
                        },
                    },
                ]}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={setDetail}
            />
            <DetailDrawer
                opened={Boolean(detail)}
                onClose={() => setDetail(null)}
                title={
                    detail
                        ? `${lookups.resolveUser(detail.userId, detail.user).title} · ${resolveMemberOrg(detail).title}`
                        : t('detail.orgMember')
                }
                footer={
                    detail ? (
                        <RemoveEntityAction
                            entityId={detail.id}
                            entityName={(() => {
                                const user = lookups.resolveUser(detail.userId, detail.user);
                                const org = resolveMemberOrg(detail);
                                return `${user.title} @ ${org.title}`;
                            })()}
                            deleteFn={deleteOrganizationMember}
                            queryKeys={['organization-members']}
                            successMessageKey="features.orgMembers.removed"
                            actionLabelKey="common.actions.removeMember"
                            onRemoved={() => setDetail(null)}
                        />
                    ) : undefined
                }
            >
                {detail ? (
                    <OrganizationMemberDetailPanel member={detail} orgById={orgById} />
                ) : null}
            </DetailDrawer>
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.orgMembers.createModalTitle')}
                size="lg"
            >
                <Stack>
                    <Select
                        label={t('fields.mode')}
                        data={[
                            { value: 'existing', label: t('features.orgMembers.modeExisting') },
                            { value: 'new', label: t('features.orgMembers.modeNew') },
                        ]}
                        value={form.values.mode}
                        onChange={(v) =>
                            form.setFieldValue('mode', (v as 'existing' | 'new') ?? 'existing')
                        }
                    />
                    <Select
                        label={t('fields.organization')}
                        required
                        searchable
                        data={(orgsQuery.data?.data ?? []).map((o) => ({
                            value: o.id,
                            label: o.name,
                        }))}
                        value={form.values.organizationId}
                        onChange={(v) => {
                            form.setFieldValue('organizationId', v ?? '');
                            form.setFieldValue('roleId', '');
                        }}
                    />
                    <Select
                        label={t('fields.organizationRole')}
                        required
                        disabled={!selectedOrgId || orgRolesQuery.isLoading}
                        placeholder={
                            selectedOrgId
                                ? t('features.orgMembers.selectRolePlaceholder')
                                : t('features.orgMembers.selectOrgFirst')
                        }
                        data={orgRoles.map((r) => ({ value: r.id, label: r.name }))}
                        value={form.values.roleId}
                        onChange={(v) => form.setFieldValue('roleId', v ?? '')}
                    />
                    {selectedOrgId && !orgRolesQuery.isLoading && orgRoles.length === 0 ? (
                        <Text size="xs" c="dimmed">
                            {t('features.orgMembers.noOrgRolesHint')}
                        </Text>
                    ) : null}
                    {form.values.mode === 'existing' ? (
                        <Select
                            label={t('fields.user')}
                            required
                            searchable
                            data={(usersQuery.data?.data ?? []).map((u) => ({
                                value: u.id,
                                label: `${u.firstName} ${u.lastName} (${u.email ?? u.id})`,
                            }))}
                            value={form.values.userId}
                            onChange={(v) => form.setFieldValue('userId', v ?? '')}
                        />
                    ) : (
                        <>
                            <TextInput label={t('fields.email')} required {...form.getInputProps('email')} />
                            <TextInput
                                label={t('fields.firstName')}
                                required
                                {...form.getInputProps('firstName')}
                            />
                            <TextInput
                                label={t('fields.lastName')}
                                required
                                {...form.getInputProps('lastName')}
                            />
                            <TextInput label={t('fields.phone')} required {...form.getInputProps('phone')} />
                        </>
                    )}
                    <Button loading={createMutation.isPending} onClick={() => createMutation.mutate()}>
                        {t('common.actions.add')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
}
